// © Microsoft Corporation. All rights reserved.
import { Base64 } from "js-base64";
import { canUseDOM } from "@msnews/isomorphic-util";
import { isSwiftKey } from "./utils/NativeAppCheck";
/**
 * inline-head, SSR-extentions and main bundles ( vendor, microsoft, commons, experiences ) are 3 differnt bundles, with different memory space.
 * If we save gJobListeners, gObservers, gAppId in simple global variables, they will not be shared between these 3 bundles.
 *
 * To solve this issue, we save these variables in window.__sapphirePeregrineState, which is shared between these 3 bundles.
 */
if (typeof window !== "undefined" && !window.__sapphirePeregrineState) {
    window.__sapphirePeregrineState = {
        gJobListeners: {},
        gObservers: {},
        gAppId: undefined,
        gInited: false
    };
}
function uuid() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c == "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
function onDataReady(jobId, result) {
    let decoded = Base64.decode(result);
    // avoid JSON.parse SyntaxError when decoded is undefined/"";
    if (decoded === undefined || decoded === "") {
        decoded = null;
    }
    const { gJobListeners, gObservers } = window.__sapphirePeregrineState;
    if (gJobListeners[jobId] && gJobListeners[jobId].length > 0) {
        gJobListeners[jobId].forEach((callback) => {
            callback(decoded);
        });
        gJobListeners[jobId] = undefined;
    }
    if (gObservers[jobId] && gObservers[jobId].length > 0) {
        gObservers[jobId].forEach((callback) => {
            callback(decoded);
        });
    }
}
function subscribeCallback(jobId, callback) {
    const { gJobListeners } = window.__sapphirePeregrineState;
    if (gJobListeners[jobId]) {
        gJobListeners[jobId].push(callback);
    }
    else {
        gJobListeners[jobId] = [callback];
    }
}
function subscribeObserver(jobId, callback) {
    const { gObservers } = window.__sapphirePeregrineState;
    if (gObservers[jobId]) {
        gObservers[jobId].push(callback);
    }
    else {
        gObservers[jobId] = [callback];
    }
}
function unSubscribeObserver(jobId) {
    const { gObservers } = window.__sapphirePeregrineState;
    gObservers[jobId] = undefined;
}
/**
 * Determine if sapphire bridge exists or not.
 */
export function isSapphire() {
    if (!canUseDOM() || isSwiftKey()) {
        return false;
    }
    const isAndroid = window.sapphireWebViewBridge?.send;
    const isIOS = window.webkit?.messageHandlers?.send?.postMessage;
    return isAndroid || isIOS;
}
/**
 * Initialize sapphire bridge.
 * You have to invoke this method before any bridge call.
 */
export function initSapphire() {
    if (window.__sapphirePeregrineState.gInited === false && isSapphire()) {
        if (!window.sapphireWebViewBridge) { // iOS will not have sapphireWebViewBridge object
            window.sapphireWebViewBridge = {};
        }
        window.sapphireWebViewBridge.$onDataReady = onDataReady;
        window.__sapphirePeregrineState.gInited = true;
    }
}
/**
 * Set default app id for this bridge instance.
 * In future, every bridge call should has a valid app id, otherwise this call will be dropped.
 * @param appId
 */
export function setDefaultAppId(appId) {
    if (window.__sapphirePeregrineState) {
        window.__sapphirePeregrineState.gAppId = appId;
    }
}
/**
 * Post job data to super app.
 * @param scenario scenario name.
 * @param data parameters for this scenario.
 * @returns job id.
 */
function postJobDataToWebView(scenario, data) {
    console.log(scenario, data);
    const jobId = uuid();
    const msgObj = {
        scenario: scenario,
        data: {
            data: data
        },
        id: jobId
    };
    if (window.__sapphirePeregrineState?.gAppId && !msgObj.data.data["appId"]) {
        msgObj.data.data["appId"] = window.__sapphirePeregrineState?.gAppId;
    }
    const jsonMsg = JSON.stringify(msgObj);
    window.webkit?.messageHandlers?.send?.postMessage && window.webkit?.messageHandlers?.send?.postMessage(jsonMsg);
    window.sapphireWebViewBridge?.send && window.sapphireWebViewBridge.send(jsonMsg);
    if (scenario !== "subscribe" && scenario !== "sendBroadcast") {
        // VPsapphireWebViewBridge is used to capture telemetry data in VP cases. Subscribe is not telemetry and can't pass telemetry validations.
        window.VPsapphireWebViewBridge?.send && window.VPsapphireWebViewBridge.send(jsonMsg);
    }
    return jobId;
}
export function invoke(method, data, callback) {
    if (!canUseDOM()) {
        return;
    }
    const jobId = postJobDataToWebView(method, data);
    if (callback) {
        subscribeCallback(jobId, callback);
    }
}
export function listen(data, handler) {
    if (!canUseDOM()) {
        return;
    }
    const handlerId = postJobDataToWebView("subscribe", data);
    subscribeObserver(handlerId, handler);
    return handlerId;
}
export function unlisten(handlerId, data, callback) {
    invoke("unSubscribe", data, (result) => {
        callback && callback(result);
    });
    unSubscribeObserver(handlerId);
}
//# sourceMappingURL=SapphireCore.js.map